import { useEffect, useState } from 'react';

import { FormError } from '@bambi/component-library/src/components/forms/FormError/FormError';
import { Combobox } from '@bambi/component-library/src/components/inputs/Combobox/Combobox';
import { StyledOption } from '@bambi/component-library/src/components/inputs/Combobox/StyledOption';
import {
  ChatBubbleBottomCenterTextIcon,
  CreditCardIcon,
} from '@heroicons/react/24/outline';
import { useFormContext } from 'react-hook-form';

import type {
  PassengerPassengerTagRead,
  PassengerRead,
} from 'src/common/external/bambi-api/bambiApi';
import { FormFieldController } from 'src/common/FormField/FormFieldController';
import { buildPassengerSelectOption } from 'src/features/add-trip/PassengerInfoFormSection/buildPassengerSelectOption';
import { usePassengerOptions } from 'src/features/add-trip/PassengerInfoFormSection/usePassengerOptions';

import type { SelectOption } from '@bambi/component-library/src/components/inputs/Select/SelectOption/SelectOption';

interface ExistingPassengerSearchProps {
  selectedPassenger: PassengerRead | null | undefined;
  onPassengerSelected: (passenger: PassengerRead | undefined) => void;
  onPassengerEdit?: (passenger: PassengerRead) => void;
  disabled?: boolean;
  allowAddPassenger?: boolean;
  id?: string;
}

export function ExistingPassengerSearch({
  selectedPassenger,
  onPassengerSelected,
  onPassengerEdit,
  disabled,
  allowAddPassenger = true,
  id,
}: ExistingPassengerSearchProps) {
  const formContext = useFormContext();
  const [value, setValue] = useState<SelectOption | undefined>(undefined);

  const { debounceSearch, options, query, queryState, setQuery } =
    usePassengerOptions(allowAddPassenger);

  useEffect(() => {
    if (selectedPassenger?.id) {
      formContext.clearErrors('passenger.id');
      setValue(buildPassengerSelectOption(selectedPassenger));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPassenger]);

  const onPassengerChange = (selectedPassengerOption: SelectOption) => {
    if (selectedPassengerOption.value === 'add-passenger' && query) {
      const trimmedQuery = query.trim();
      const querySplit = trimmedQuery.split(/\s+/);
      // Sometimes folks use multiple names, so assume the first value is the
      // first name, and join the remaining names as the last name
      const firstName = querySplit[0];
      querySplit.shift();
      const lastName = querySplit.join(' ');
      const temp = {
        first_name: firstName,
        last_name: lastName,
        tags: [] as PassengerPassengerTagRead[],
      };

      onPassengerEdit?.(temp as PassengerRead);

      setValue({ label: '', value: '' });
      setQuery('');
      return;
    }

    const passengers = queryState.data?.results || [];
    const passenger = passengers.find(
      (p) => p.id === selectedPassengerOption.value
    );
    onPassengerSelected(passenger);
  };

  return (
    <FormFieldController
      name="passenger.id"
      rules={{
        required: 'Passenger is required',
      }}
    >
      {({ field, fieldState }) => {
        return (
          <>
            <Combobox
              id={id}
              value={value}
              placeholder={
                allowAddPassenger
                  ? 'Search for a passenger or add a new one here'
                  : 'Search for a passenger'
              }
              options={options}
              onQueryChange={debounceSearch}
              onChange={(selected) => {
                onPassengerChange(selected);
                field.onChange(selected.value);
              }}
              error={fieldState.error?.message?.toString()}
              dataTestId="existing-passenger-search"
              disabled={disabled}
              renderOption={(option, selected, active) => {
                const passengers = queryState.data?.results || [];
                const passenger = passengers.find((p) => p.id === option.value);

                return (
                  <StyledOption
                    key={option.value}
                    option={option}
                    selected={selected}
                    active={active}
                    startAddornment={
                      <PassengerListItemAdornment passenger={passenger} />
                    }
                  />
                );
              }}
            />
            {fieldState.error?.message && (
              <FormError error={fieldState.error?.message}></FormError>
            )}
          </>
        );
      }}
    </FormFieldController>
  );
}

export function PassengerListItemAdornment({
  passenger,
}: {
  passenger?: PassengerRead;
}) {
  if (!passenger) {
    return null;
  }

  return (
    <span className="flex flex-row gap-2">
      {passenger.has_tilled_account ? (
        <CreditCardIcon
          className="h-4 w-4"
          title="Payment method on file"
          data-testid="payment-method-icon"
        />
      ) : null}
      {passenger.allow_sms_message ? (
        <ChatBubbleBottomCenterTextIcon
          className="h-4 w-4"
          title="SMS enabled"
          data-testid="sms-icon"
        />
      ) : null}
    </span>
  );
}
